import React, { useEffect, useState, useCallback, useMemo } from "react";
import { App, Spin, Button, Dropdown, InputNumber } from "antd";
import { PlusOutlined, MinusOutlined, MoreOutlined } from "@ant-design/icons";
import FirestoreService, { ArcherScore } from "../../_helpers/firestoreService";
import { sortArchersByScore } from "../../_helpers/utils";
import "./scoreboard.scss";

interface ScoreBoardProps {
  competition: any;
  competitors: any[];
  zurkhaiIndex: number;
  currentUserId: number;
  userRole: string;
  onogUe: number;
  zurhaiTotal?: number;
  onZurkhaiChange?: (newIndex: number) => void;
  isFinished?: boolean;
}

interface ActiveScoreEntry {
  archerId: number;
  arrowIndex: number;
  clicks: number[]; // Array of 2 clicks: 1 = scored, 0 = missed, -1 = not attended
  clickCount: number;
  archer: any; // Store full archer object for metadata
}

const ScoreBoard: React.FC<ScoreBoardProps> = ({
  competition,
  competitors,
  zurkhaiIndex,
  currentUserId,
  userRole,
  onogUe,
  zurhaiTotal,
  onZurkhaiChange,
  isFinished = false,
}) => {
  const { message } = App.useApp();
  const [scores, setScores] = useState<Map<number, ArcherScore>>(new Map());
  const [loading, setLoading] = useState(true);
  const [activeEntry, setActiveEntry] = useState<ActiveScoreEntry | null>(null);
  const [focusedCell, setFocusedCell] = useState<{
    archerId: number;
    arrowIndex: number;
  } | null>(null);
  const [editingTovkh, setEditingTovkh] = useState<{
    archerId: number;
    value: number;
  } | null>(null);

  // Subscribe to real-time score updates
  useEffect(() => {
    if (!competition?.id) return;

    const unsubscribe = FirestoreService.subscribeToZurkhaiScores(
      competition.id,
      zurkhaiIndex,
      (scoresData) => {
        scoresData.sort((a, b) => a.index - b.index);
        const scoresMap = new Map<number, ArcherScore>();
        scoresData.forEach((score) => {
          scoresMap.set(score.userId, score);
        });
        setScores(scoresMap);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [competition?.id, zurkhaiIndex]);

  const isMainReferee = () => {
    return (
      userRole === "admin" ||
      userRole === "superadmin" ||
      competition.referee_id === currentUserId
    );
  };

  const canEditCell = (archerId: number, arrowIndex: number) => {
    // If competition is finished, no one can edit
    if (isFinished) {
      return false;
    }
    // Main referee can edit any cell
    if (isMainReferee()) {
      return true;
    }
    // Zurkhai referee can only edit the focused cell
    return (
      focusedCell?.archerId === archerId &&
      focusedCell?.arrowIndex === arrowIndex
    );
  };

  const openScoreEntry = useCallback(
    (archer: any, arrowIndex: number) => {
      // Check if user can edit this cell - early return for performance
      if (archer.isAbsent) return;
      if (!canEditCell(archer.id, arrowIndex)) return;

      // Get existing clicks for this arrow, or default to [-1, -1]
      const existingClicks = archer.clicks?.[arrowIndex] || [-1, -1];

      // Optimize clickCount calculation - only count non-null, non--1 values
      let clickCount = 0;
      for (let i = 0; i < existingClicks.length; i++) {
        if (existingClicks[i] !== -1 && existingClicks[i] !== null) {
          clickCount++;
        }
      }

      // For main referees (admin), allow re-editing by resetting clickCount to allow new clicks
      // But keep the existing clicks visible so admin can see current values
      const editableClickCount =
        isMainReferee() && clickCount === 2 ? 0 : clickCount;

      // Immediate state update - use functional update to ensure we always get the latest state
      setActiveEntry((prevEntry) => {
        // If clicking on the same cell, still update to refresh the state
        if (
          prevEntry?.archerId === archer.id &&
          prevEntry?.arrowIndex === arrowIndex
        ) {
          return {
            archerId: archer.id,
            arrowIndex,
            clicks: existingClicks,
            clickCount: editableClickCount,
            archer: archer,
          };
        }
        // New cell - always update
        return {
          archerId: archer.id,
          arrowIndex,
          clicks: existingClicks, // Keep existing clicks visible
          clickCount: editableClickCount, // Reset count for admin to allow re-editing
          archer: archer, // Store full archer object
        };
      });

      // For admin users, also update focusedCell to ensure consistency
      if (isMainReferee()) {
        setFocusedCell({
          archerId: archer.id,
          arrowIndex,
        });
      }
    },
    [focusedCell, userRole, competition, currentUserId]
  );

  const moveToNextCell = () => {
    if (!focusedCell) return;

    const currentArcherIndex = competitors.findIndex(
      (c) => c.id === focusedCell.archerId
    );

    // Try to move to next archer in same column
    if (currentArcherIndex < competitors.length - 1) {
      setFocusedCell({
        archerId: competitors[currentArcherIndex + 1].id,
        arrowIndex: focusedCell.arrowIndex,
      });
    }
    // Column finished, move to next column (top archer)
    else if (focusedCell.arrowIndex < onogUe - 1) {
      setFocusedCell({
        archerId: competitors[0].id,
        arrowIndex: focusedCell.arrowIndex + 1,
      });
    }
    // All done - stay at last cell
  };

  const skipCell = () => {
    moveToNextCell();
    setActiveEntry(null);
  };

  // Auto-focus first unscored cell for zurkhai referees (but not when finished)
  useEffect(() => {
    if (isFinished) return; // Don't auto-focus when competition is finished
    if (!isMainReferee() && competitors.length > 0 && scores.size > 0) {
      // Find the first cell that doesn't have a score (hasn't been scored yet)
      let foundEmptyCell = false;

      // Go through each arrow index (column)
      for (let arrowIndex = 0; arrowIndex < onogUe; arrowIndex++) {
        // Go through each competitor (row)
        for (let i = 0; i < competitors.length; i++) {
          const competitor = competitors[i];
          const archerScore = scores.get(competitor.id);

          // Check if archer is absent - skip if absent
          if (archerScore?.isAbsent) {
            continue;
          }

          // Get clicks for this arrow
          const clicks = archerScore?.clicks?.[arrowIndex] || [-1, -1];

          // Check if this cell has been scored (has valid clicks that are not both -1)
          // A cell is considered "done" if at least one click is not -1
          const isScored = clicks[0] !== -1 || clicks[1] !== -1;

          // If this cell has not been scored yet, focus on it
          if (!isScored) {
            setFocusedCell({
              archerId: competitor.id,
              arrowIndex: arrowIndex,
            });
            foundEmptyCell = true;
            break;
          }
        }

        if (foundEmptyCell) break;
      }

      // If no empty cell found, don't focus on anything (competition is over)
      if (!foundEmptyCell) {
        setFocusedCell(null);
      }
    }
  }, [competitors, scores, onogUe]);

  // Auto-open focused cell for zurkhai referees (but not when finished)
  useEffect(() => {
    if (isFinished) return; // Don't auto-open when competition is finished
    if (!isMainReferee() && focusedCell && competitors.length > 0) {
      const archer = competitors.find((c) => c.id === focusedCell.archerId);
      if (archer) {
        openScoreEntry(archer, focusedCell.arrowIndex);
      }
    }
  }, [focusedCell, competitors, openScoreEntry]);

  const handleClick = async (isHit: boolean) => {
    if (!activeEntry) return;

    // For main referees (admin), allow editing even if clickCount is 2
    // For others, prevent if already at 2 clicks
    if (!isMainReferee() && activeEntry.clickCount >= 2) return;

    const newClicks = [...activeEntry.clicks];
    // If admin is re-editing (clickCount was reset to 0), overwrite existing values
    const targetIndex = activeEntry.clickCount < 2 ? activeEntry.clickCount : 0;
    newClicks[targetIndex] = isHit ? 1 : 0; // 1 = scored, 0 = missed
    const newClickCount = activeEntry.clickCount + 1;

    const updatedEntry = {
      ...activeEntry,
      clicks: newClicks,
      clickCount: newClickCount,
    };

    setActiveEntry(updatedEntry);

    // Auto-submit after 2 clicks
    if (newClickCount === 2) {
      setTimeout(() => submitScore(updatedEntry), 200);
    }
  };

  const calculateScore = (clicks: number[]): number => {
    // Count how many scored (1s) - max 2 points
    // -1 means not attended, so don't count it
    return clicks.filter((click) => click === 1).length;
  };

  const handleMarkAbsent = async (archerId: number, isAbsent: boolean) => {
    if (!competition?.id) return;

    try {
      // Only update the isAbsent status, don't modify existing scores/clicks
      await FirestoreService.markArcherAbsent(
        competition.id,
        zurkhaiIndex,
        archerId,
        isAbsent,
        currentUserId
      );

      message.success(
        isAbsent ? "Харваач ирээгүй гэж тэмдэглэлээ" : "Харваач идэвхжлээ"
      );
    } catch (error) {
      console.error("Error marking archer absent:", error);
      message.error("Алдаа гарлаа");
    }
  };

  const handleUpdateTovkh = async (archerId: number, tovkhCount: number) => {
    if (!competition?.id) return;

    try {
      await FirestoreService.updateTovkhCount(
        competition.id,
        zurkhaiIndex,
        archerId,
        tovkhCount,
        currentUserId
      );

      message.success("Товх амжилттай шинэчлэгдлээ");
      setEditingTovkh(null);
    } catch (error) {
      console.error("Error updating tovkh:", error);
      message.error("Алдаа гарлаа");
    }
  };

  const submitScore = async (entry: ActiveScoreEntry) => {
    if (!competition?.id) return;

    try {
      const archerScore = scores.get(entry.archerId);

      // Create a full-length array with correct size, filling with existing scores or 0
      const existingScores = archerScore?.scores || [];
      const newScores = Array(onogUe)
        .fill(0)
        .map((_, idx) => existingScores[idx] ?? 0);

      // Create full-length clicks array
      const existingClicks = archerScore?.clicks || [];
      const newClicks = Array(onogUe)
        .fill(null)
        .map((_, idx) => existingClicks[idx] || [-1, -1]);

      // Calculate score for this arrow (0-2)
      const arrowScore = calculateScore(entry.clicks);
      newScores[entry.arrowIndex] = arrowScore;
      newClicks[entry.arrowIndex] = entry.clicks;

      // Update hanaPoints (when score is 2 - both hits)
      const hanaPoints = archerScore ? [...archerScore.hanaPoints] : [];
      if (arrowScore === 2) {
        if (!hanaPoints.includes(entry.arrowIndex)) {
          hanaPoints.push(entry.arrowIndex);
        }
      } else {
        const index = hanaPoints.indexOf(entry.arrowIndex);
        if (index > -1) {
          hanaPoints.splice(index, 1);
        }
      }

      // Build metadata object, only if we have the archer data
      let metadata: any = undefined;

      if (
        entry.archer &&
        entry.archer.firstName &&
        entry.archer.lastName &&
        entry.archer.gender
      ) {
        metadata = {
          firstName: entry.archer.firstName,
          lastName: entry.archer.lastName,
          gender: entry.archer.gender,
        };

        // Only add optional fields if they have values
        if (entry.archer.team?.id) {
          metadata.teamId = entry.archer.team.id;
        }
        if (entry.archer.team?.name) {
          metadata.teamName = entry.archer.team.name;
        }
        if (entry.archer.rank?.id) {
          metadata.rankId = entry.archer.rank.id;
        }
        if (entry.archer.rank?.name) {
          metadata.rankName = entry.archer.rank.name;
        }
      }

      // Update in Firestore
      await FirestoreService.updateScore(
        competition.id,
        zurkhaiIndex,
        entry.archerId,
        newScores,
        newClicks,
        hanaPoints,
        archerScore?.hasPoints || [],
        currentUserId,
        metadata
      );

      message.success("Оноо хадгалагдлаа");
      setActiveEntry(null);

      // Auto-move to next cell for zurkhai referees
      if (!isMainReferee()) {
        moveToNextCell();
      }
    } catch (error) {
      console.error("Error saving score:", error);
      message.error("Оноо хадгалахад алдаа гарлаа");
    }
  };

  const canEditScores = () => {
    // Admin, superadmin and main referee can edit all scores
    if (
      userRole === "admin" ||
      userRole === "superadmin" ||
      competition.referee_id === currentUserId
    ) {
      return true;
    }
    // Zurkhai referee can only edit their assigned zurkhai
    const refereesIds = competition.referees_ids || [];
    const assignedRefereeId = refereesIds[zurkhaiIndex - 1];
    return assignedRefereeId === currentUserId;
  };

  // Memoize enrichedCompetitors to avoid recalculating on every render
  // DON'T sort them - keep original order for the scoring table
  const enrichedCompetitors = useMemo(() => {
    // If scores exist in Firestore, only show archers from Firestore (prevent auto-addition)
    let archers: any[] = [];

    if (scores.size > 0) {
      scores.forEach((archerScore, userId) => {
        // Find the archer in competitors to get full data
        const compData = competitors.find((c: any) => c.id === userId);

        const existingScores = archerScore?.scores || [];
        const existingClicks = archerScore?.clicks || [];

        // Ensure scores array has exactly onogUe elements
        const paddedScores = Array(onogUe)
          .fill(0)
          .map((_, idx) => existingScores[idx] || 0);
        const paddedClicks = Array(onogUe)
          .fill(null)
          .map((_, idx) => existingClicks[idx] || [-1, -1]);

        // Recalculate totalScore: only count scores where archer actually participated (not [-1, -1])
        const recalculatedTotalScore = paddedScores.reduce(
          (sum, score, idx) => {
            const clicks = paddedClicks[idx] || [-1, -1];
            // Only count if not marked as not attended (both clicks are not -1)
            if (clicks[0] !== -1 || clicks[1] !== -1) {
              return sum + score;
            }
            return sum;
          },
          0
        );

        if (compData) {
          archers.push({
            ...compData,
            index: archerScore.index,
            totalScore: recalculatedTotalScore,
            scores: paddedScores,
            clicks: paddedClicks,
            hanaPoints: archerScore?.hanaPoints || [],
            tovkhCount: archerScore?.tovkhCount || 0,
            isAbsent: archerScore?.isAbsent || false,
          });
        } else {
          // If archer not found in current competitors (removed from competition),
          // create minimal archer object from Firestore data
          archers.push({
            id: userId,
            index: archerScore.index,
            firstName: archerScore.firstName || "",
            lastName: archerScore.lastName || "",
            gender: archerScore.gender || "male",
            team: archerScore.teamName
              ? { id: archerScore.teamId, name: archerScore.teamName }
              : undefined,
            rank: archerScore.rankName
              ? { id: archerScore.rankId, name: archerScore.rankName }
              : undefined,
            totalScore: recalculatedTotalScore,
            scores: paddedScores,
            clicks: paddedClicks,
            hanaPoints: archerScore?.hanaPoints || [],
            tovkhCount: archerScore?.tovkhCount || 0,
            isAbsent: archerScore?.isAbsent || false,
          });
        }
      });
    } else {
      // If no Firestore scores yet, show from competitors prop
      archers = competitors.map((comp) => {
        const archerScore = scores.get(comp.id);
        const existingScores = archerScore?.scores || [];
        const existingClicks = archerScore?.clicks || [];

        // Ensure scores array has exactly onogUe elements
        const paddedScores = Array(onogUe)
          .fill(0)
          .map((_, idx) => existingScores[idx] || 0);
        const paddedClicks = Array(onogUe)
          .fill(null)
          .map((_, idx) => existingClicks[idx] || [-1, -1]);

        // Recalculate totalScore: only count scores where archer actually participated (not [-1, -1])
        const recalculatedTotalScore = paddedScores.reduce(
          (sum, score, idx) => {
            const clicks = paddedClicks[idx] || [-1, -1];
            // Only count if not marked as not attended (both clicks are not -1)
            if (clicks[0] !== -1 || clicks[1] !== -1) {
              return sum + score;
            }
            return sum;
          },
          0
        );

        return {
          ...comp,
          totalScore: recalculatedTotalScore,
          scores: paddedScores,
          clicks: paddedClicks,
          hanaPoints: archerScore?.hanaPoints || [],
          tovkhCount: archerScore?.tovkhCount || 0,
          isAbsent: archerScore?.isAbsent || false,
        };
      });
    }

    const positionMap = new Map<number, number>();
    archers.forEach((archer, index) => {
      const archerId =
        typeof archer.id === "string" ? parseInt(archer.id) : archer.id;
      positionMap.set(archerId, index + 1);
    });

    // Add positions to original (unsorted) archers
    archers.forEach((archer) => {
      const archerId =
        typeof archer.id === "string" ? parseInt(archer.id) : archer.id;
      archer.position = positionMap.get(archerId) || 0;
    });

    // Return archers in ORIGINAL order (as they appear in competitors prop)
    return archers;
  }, [competitors, scores, onogUe]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <p>Уншиж байна...</p>
      </div>
    );
  }

  return (
    <div className="score-board">
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <h3>Оноо бүртгэх самбар</h3>
        </div>
        <p>
          {competition?.name} | {competition?.location}
        </p>
        {isMainReferee() &&
          zurhaiTotal &&
          zurhaiTotal > 1 &&
          onZurkhaiChange && (
            <div
              style={{
                overflowX: "auto",
                marginTop: 16,
                marginBottom: 16,
                borderBottom: "1px solid #d9d9d9",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  minWidth: "max-content",
                }}
              >
                {Array.from({ length: zurhaiTotal }, (_, i) => i + 1).map(
                  (index) => (
                    <div
                      key={index}
                      onClick={() => onZurkhaiChange(index)}
                      style={{
                        padding: "8px 16px",
                        cursor: "pointer",
                        borderBottom:
                          zurkhaiIndex === index
                            ? "2px solid #1890ff"
                            : "2px solid transparent",
                        color: zurkhaiIndex === index ? "#1890ff" : "#595959",
                        fontWeight: zurkhaiIndex === index ? 600 : 400,
                        transition: "all 0.3s",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => {
                        if (zurkhaiIndex !== index) {
                          e.currentTarget.style.color = "#1890ff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (zurkhaiIndex !== index) {
                          e.currentTarget.style.color = "#595959";
                        }
                      }}
                    >
                      Зурхай {index}
                    </div>
                  )
                )}
              </div>
            </div>
          )}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="score-table">
          <thead>
            <tr>
              <th rowSpan={2}>№</th>
              <th rowSpan={2}>Харваачийн овог нэр</th>
              <th rowSpan={2}>Харьяалал</th>
              <th rowSpan={2}>Цол, зэрэг</th>
              <th
                colSpan={
                  onogUe > 10
                    ? onogUe + 3
                    : onogUe > 8
                    ? onogUe + 2
                    : onogUe > 5
                    ? onogUe + 1
                    : onogUe
                }
              >
                Оног үе
              </th>
              <th rowSpan={2}>Нийт оноо</th>
              <th rowSpan={2}>Товх</th>
              <th rowSpan={2}>Эзэлсэн байр</th>
              {isMainReferee() && !isFinished && <th rowSpan={2}>Үйлдэл</th>}
            </tr>
            <tr>
              {Array.from({ length: onogUe }, (_, i) => (
                <React.Fragment key={i}>
                  <th>{i + 1}</th>
                  {/* Add summary column headers after specific indices */}
                  {i === 4 && onogUe > 5 && (
                    <th
                      style={{ backgroundColor: "#e0e0e0", fontSize: "0.65em" }}
                    >
                      10 сумны нийлбэр
                    </th>
                  )}
                  {i === 7 && onogUe > 8 && (
                    <th
                      style={{ backgroundColor: "#e0e0e0", fontSize: "0.65em" }}
                    >
                      16 сумны нийлбэр
                    </th>
                  )}
                  {i === 9 && onogUe > 10 && (
                    <th
                      style={{ backgroundColor: "#e0e0e0", fontSize: "0.65em" }}
                    >
                      20 сумны нийлбэр
                    </th>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {enrichedCompetitors.map((archer, index) => {
              const archerId =
                typeof archer.id === "string" ? parseInt(archer.id) : archer.id;

              // Calculate cumulative sums
              const sum10 = archer.scores
                .slice(0, 10)
                .reduce((sum: number, score: number, idx: number) => {
                  const clicks = archer.clicks?.[idx] || [-1, -1];
                  if (clicks[0] !== -1 || clicks[1] !== -1) {
                    return sum + score;
                  }
                  return sum;
                }, 0);

              const sum16 = archer.scores
                .slice(0, 16)
                .reduce((sum: number, score: number, idx: number) => {
                  const clicks = archer.clicks?.[idx] || [-1, -1];
                  if (clicks[0] !== -1 || clicks[1] !== -1) {
                    return sum + score;
                  }
                  return sum;
                }, 0);

              const sum20 = archer.scores
                .slice(0, 20)
                .reduce((sum: number, score: number, idx: number) => {
                  const clicks = archer.clicks?.[idx] || [-1, -1];
                  if (clicks[0] !== -1 || clicks[1] !== -1) {
                    return sum + score;
                  }
                  return sum;
                }, 0);

              return (
                <tr key={archer.id}>
                  <td>{index + 1}</td>
                  <td>
                    {archer.lastName} {archer.firstName}
                  </td>
                  <td>
                    {typeof archer.team === "object" && archer.team
                      ? (archer.team as any).name
                      : archer.team || "-"}
                  </td>
                  <td>
                    {typeof archer.rank === "object" && archer.rank
                      ? (archer.rank as any).name
                      : archer.rank || "-"}
                  </td>
                  {archer.scores.map((score: number, arrowIndex: number) => {
                    const isActive =
                      activeEntry?.archerId === archerId &&
                      activeEntry?.arrowIndex === arrowIndex;
                    const isFocused =
                      focusedCell?.archerId === archerId &&
                      focusedCell?.arrowIndex === arrowIndex;
                    const isEditable =
                      canEditCell(archerId, arrowIndex) && !archer.isAbsent;
                    const clicks = archer.clicks?.[arrowIndex] || [-1, -1];
                    // Check if this arrow is marked as not attended (-1)
                    const isNotAttended = clicks[0] === -1 && clicks[1] === -1;
                    const clickCount = clicks.filter(
                      (c: number) => c !== -1 && c !== null
                    ).length;
                    const displayScore = isNotAttended
                      ? ""
                      : clickCount > 0
                      ? `${score}/${clickCount}`
                      : "";

                    return (
                      <React.Fragment key={`${archer.id}-${arrowIndex}`}>
                        <td
                          className={`score-cell ${
                            isEditable ? "editable" : ""
                          } ${isActive ? "active" : ""} ${
                            isFocused && !isMainReferee() ? "focused" : ""
                          }`}
                          style={{
                            padding: isActive ? "4px" : "8px 12px",
                          }}
                          onClick={(e) => {
                            // Also handle click on the td element for better reliability
                            if (isEditable && !isActive) {
                              e.preventDefault();
                              e.stopPropagation();
                              setTimeout(() => {
                                openScoreEntry(archer, arrowIndex);
                              }, 0);
                            }
                          }}
                        >
                          {isActive ? (
                            <div className="score-entry-inline">
                              <div className="score-symbols">
                                {activeEntry.clicks.map((click, idx) => (
                                  <span
                                    key={idx}
                                    className={`symbol ${
                                      click !== null && click !== -1
                                        ? "filled"
                                        : ""
                                    }`}
                                  >
                                    {click === 1
                                      ? "+"
                                      : click === 0
                                      ? "-"
                                      : "·"}
                                  </span>
                                ))}
                              </div>
                              {(activeEntry.clickCount < 2 ||
                                isMainReferee()) && (
                                <div className="button-row">
                                  <button
                                    className="score-btn hit"
                                    onClick={() => handleClick(true)}
                                  >
                                    <PlusOutlined />
                                  </button>
                                  <button
                                    className="score-btn miss"
                                    onClick={() => handleClick(false)}
                                  >
                                    <MinusOutlined />
                                  </button>
                                  {!isMainReferee() && (
                                    <button
                                      className="score-btn skip"
                                      onClick={skipCell}
                                      title="Алгасах"
                                    >
                                      &raquo;
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isEditable) {
                                  // Use setTimeout to ensure state updates happen in the next tick
                                  // This helps with race conditions when switching between cells
                                  setTimeout(() => {
                                    openScoreEntry(archer, arrowIndex);
                                  }, 0);
                                }
                              }}
                              onMouseDown={(e) => {
                                // Prevent default to avoid any browser focus issues
                                if (isEditable) {
                                  e.preventDefault();
                                }
                              }}
                              style={{
                                cursor: isEditable ? "pointer" : "default",
                              }}
                            >
                              {displayScore}
                            </div>
                          )}
                        </td>

                        {/* Add summary columns after specific arrow indices */}
                        {arrowIndex === 4 && onogUe > 5 && (
                          <td
                            className="summary-cell"
                            style={{ backgroundColor: "#f0f0f0" }}
                          >
                            {sum10}
                          </td>
                        )}
                        {arrowIndex === 7 && onogUe > 8 && (
                          <td
                            className="summary-cell"
                            style={{ backgroundColor: "#f0f0f0" }}
                          >
                            {sum16}
                          </td>
                        )}
                        {arrowIndex === 9 && onogUe > 10 && (
                          <td
                            className="summary-cell"
                            style={{ backgroundColor: "#f0f0f0" }}
                          >
                            {sum20}
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <td className="total-score">{archer.totalScore}</td>
                  <td>
                    {isMainReferee() && !isFinished ? (
                      editingTovkh?.archerId === archerId ? (
                        <InputNumber
                          autoFocus
                          min={0}
                          size="small"
                          value={editingTovkh?.value || 0}
                          onChange={(value) =>
                            setEditingTovkh({
                              archerId: archerId,
                              value: value || 0,
                            })
                          }
                          onPressEnter={() =>
                            editingTovkh &&
                            handleUpdateTovkh(archerId, editingTovkh.value)
                          }
                          onBlur={() =>
                            editingTovkh &&
                            handleUpdateTovkh(archerId, editingTovkh.value)
                          }
                          style={{ width: 60 }}
                        />
                      ) : (
                        <span
                          onClick={() =>
                            setEditingTovkh({
                              archerId: archerId,
                              value: archer.tovkhCount,
                            })
                          }
                          style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          title="Засах бол дарна уу"
                        >
                          {archer.tovkhCount}
                        </span>
                      )
                    ) : (
                      archer.tovkhCount
                    )}
                  </td>
                  <td>{archer.position}</td>
                  {isMainReferee() && !isFinished && (
                    <td>
                      <Dropdown
                        menu={{
                          items: [
                            archer.isAbsent
                              ? {
                                  key: "activate",
                                  label: "Идэвхжүүлэх",
                                  onClick: () =>
                                    handleMarkAbsent(archerId, false),
                                }
                              : {
                                  key: "absent",
                                  label: "Ирээгүй",
                                  onClick: () =>
                                    handleMarkAbsent(archerId, true),
                                },
                          ],
                        }}
                        trigger={["click"]}
                      >
                        <Button
                          type="text"
                          icon={<MoreOutlined />}
                          style={{ border: "none", boxShadow: "none" }}
                        />
                      </Dropdown>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScoreBoard;
